<template>
  <b-row>
    <b-col
      cols="6"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-surname`"
        :name="`${$t('order.patient-info.surname')} (${patientIndex + 1})`"
        :rules="fullValidation ? 'required|max:50' : 'max:50'"
      >
        <label :for="`patientInfoSurname-${patientIndex}`">
          {{ $t('order.patient-info.surname') }}
        </label>
        <b-form-input
          :id="`patientInfoSurname-${patientIndex}`"
          v-model="patient.surname"
          type="text"
          maxlength="50"
          :placeholder="$t('order.patient-info.surname')"
          autocomplete="off"
          :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
          @change="commitHandler()"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      cols="6"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-name`"
        :name="`${$t('order.patient-info.name')} (${patientIndex + 1})`"
        :rules="fullValidation ? 'required|max:50' : 'max:50'"
      >
        <label :for="`patientInfoName-${patientIndex}`">
          {{ $t('order.patient-info.name') }}
        </label>
        <b-form-input
          :id="`patientInfoName-${patientIndex}`"
          v-model="patient.name"
          type="text"
          maxlength="50"
          :placeholder="$t('order.patient-info.name')"
          autocomplete="off"
          :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
          @change="commitHandler()"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      cols="12"
      class="pb-1"
    >
      <validation-provider
        ref="peselValidationProvider"
        #default="{ errors }"
        :vid="`patient-${patientIndex}-pesel`"
        :name="`${$t('order.patient-info.pesel')} (${patientIndex + 1})`"
        :rules="peselValidationRules"
      >
        <label :for="`patientInfoPesel-${patientIndex}`">
          {{ $t('order.patient-info.pesel') }}
        </label>

        <b-form-input
          :id="`patientInfoPesel-${patientIndex}`"
          v-model="patient.pesel"
          type="text"
          :name="$t('order.patient-info.pesel')"
          :disabled="isPeselFieldDisabled"
          @change="handlePeselChange"
        />
        <small
          class="order-patient-form__validation-message"
          :class="errors[0] ? 'text-danger' : 'invisible'"
        >
          {{ errors[0] }}
        </small>
      </validation-provider>

      <b-form-checkbox
        v-model="patient.patientData.noPesel"
        class="mt-50 order-patient-form__checkbox"
        :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
        @change="handleNoPeselChange"
      >
        {{ $t('order.patient-info.no-pesel') }}
      </b-form-checkbox>
    </b-col>

    <b-col
      cols="12"
      md="6"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-age`"
        :name="`${$t('order.patient-info.age-in-years')} (${patientIndex + 1})`"
        :rules="'max_value:120'"
      >
        <label :for="`patientInfoAge-${patientIndex}`">
          {{ $t('order.patient-info.age-in-years') }}
        </label>
        <b-input-group>
          <b-form-input
            :id="`patientInfoAge-${patientIndex}`"
            v-model="patient.age"
            type="number"
            maxlength="3"
            :placeholder="$t('order.patient-info.age-in-years')"
            autocomplete="off"
            :disabled="isAgeAndSexDisabled"
            @change="commitHandler()"
          />
          <b-input-group-append v-if="!isAgeAndSexDisabled">
            <b-button
              variant="outline-primary"
              class="px-50"
              @click="convertFromAnnual(patient)"
            >
              <feather-icon icon="RefreshCwIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      cols="12"
      md="6"
      class="pb-1"
    >
      <label>
        {{ $t('order.patient-info.sex') }}
      </label>
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-sex`"
        :name="$t('order.patient-info.sex')"
        :rules="fullValidation ? 'required' : ''"
      >
        <b-form-radio-group
          v-model="patient.sex"
          :name="`patientInfoSex-${patientIndex}`"
          :disabled="isAgeAndSexDisabled"
          @change="commitHandler()"
        >
          <b-form-radio
            value="M"
            class="mt-50"
          >
            {{ $t('order.patient-info.male') }}
          </b-form-radio>
          <b-form-radio
            value="F"
            class="mt-50"
          >
            {{ $t('order.patient-info.female') }}
          </b-form-radio>
        </b-form-radio-group>
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      cols="12"
      md="6"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-weight`"
        :name="`${$t('order.patient-info.weight')} (${patientIndex + 1})`"
        :rules="fullValidation ? 'max_value:500' : ''"
      >
        <label :for="`patientInfoWeight-${patientIndex}`">
          {{ $t('order.patient-info.weight') }}
        </label>
        <b-input-group>
          <b-form-input
            :id="`patientInfoWeight-${patientIndex}`"
            v-model="patient.weight"
            type="text"
            inputmode="decimal"
            :placeholder="$t('order.patient-info.weight')"
            autocomplete="off"
            :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
            @change="commitHandler()"
          />
          <b-input-group-append is-text>
            kg
          </b-input-group-append>
        </b-input-group>
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      cols="12"
      md="6"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-state`"
        :name="$t('order.patient-info.health-condition.patient-state.title')"
      >
        <label :for="`patientInfoState-${patientIndex}`">
          {{ $t('order.patient-info.health-condition.patient-state.title') }}
        </label>
        <v-select
          :id="`patientInfoState-${patientIndex}`"
          v-model="patient.healthCondition.patientState"
          :options="patientStateOptions"
          :clearable="true"
          :searchable="false"
          :reduce="option => option.value"
          :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
          @input="commitHandler()"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>

    <b-col
      v-if="isMultiTransport"
      cols="12"
      class="pb-1"
    >
      <validation-provider
        #default="{ errors }"
        :vid="`patient-${patientIndex}-note`"
        :name="`${$t('order.patient-info.note')} (${patientIndex + 1})`"
        rules="max:255"
      >
        <label :for="`patientInfoNote-${patientIndex}`">
          {{ $t('order.patient-info.note') }}
        </label>
        <b-form-input
          :id="`patientInfoNote-${patientIndex}`"
          v-model="patient.notes"
          type="text"
          maxlength="255"
          :placeholder="$t('order.patient-info.note')"
          :disabled="isDisabledFn(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
          @change="commitHandler()"
        />
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-col>
  </b-row>
</template>

<script>
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import { resolvePatientDemographicsFromPesel } from '@/helpers/pesel'

export default {
  name: 'OrderPatientForm',
  components: {
    ValidationProvider,
    vSelect,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
    patientIndex: {
      type: Number,
      required: true,
    },
    fullValidation: {
      type: Boolean,
      default: false,
    },
    isMultiTransport: {
      type: Boolean,
      default: false,
    },
    statusesWhenDispatcherCanEdit: {
      type: Array,
      default: () => [],
    },
    isDisabledFn: {
      type: Function,
      required: true,
    },
    commitHandler: {
      type: Function,
      required: true,
    },
    convertFromAnnual: {
      type: Function,
      required: true,
    },
    peselReferenceDate: {
      type: [String, Number, Date],
      default: null,
    },
  },
  computed: {
    organizationConfig() {
      return this.$store.getters['appConfig/config'] || {}
    },
    isPeselFieldDisabled() {
      return this.patient?.patientData?.noPesel || this.isDisabledFn(this.statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])
    },
    peselValidationRules() {
      if (this.patient?.patientData?.noPesel) {
        return ''
      }

      return this.fullValidation ? (this.organizationConfig?.frontendValidation?.order?.patientInfo?.peselRules ?? 'pesel') : 'pesel'
    },
    resolvedDemographicsFromPesel() {
      if (this.patient?.patientData?.noPesel) {
        return null
      }

      return resolvePatientDemographicsFromPesel(this.patient?.pesel, this.peselReferenceDate)
    },
    isAgeAndSexDisabled() {
      return this.isDisabledFn(this.statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])
        || !!this.resolvedDemographicsFromPesel
    },
    patientStateOptions() {
      return [
        {
          value: 'walking',
          label: this.$t('order.patient-info.health-condition.patient-state.walking'),
        },
        {
          value: 'sittingDown',
          label: this.$t('order.patient-info.health-condition.patient-state.sittingDown'),
        },
        {
          value: 'lyingDown',
          label: this.$t('order.patient-info.health-condition.patient-state.lyingDown'),
        },
      ]
    },
  },
  watch: {
    patient: {
      immediate: true,
      handler(patient) {
        this.ensurePatientStructures(patient)
      },
    },
  },
  methods: {
    handlePeselChange() {
      this.syncDemographicsFromPesel()
      this.commitHandler()
    },
    handleNoPeselChange() {
      this.patient.pesel = null
      this.resetPeselValidation()
      this.commitHandler()
    },
    syncDemographicsFromPesel() {
      const demographics = this.resolvedDemographicsFromPesel
      if (!demographics) {
        return
      }

      this.$set(this.patient, 'age', demographics.age)
      this.$set(this.patient, 'sex', demographics.sex)
    },
    ensurePatientStructures(patient) {
      if (!patient) {
        return
      }

      if (!patient.patientData || typeof patient.patientData !== 'object') {
        this.$set(patient, 'patientData', {})
      }

      if (!patient.healthCondition || typeof patient.healthCondition !== 'object') {
        this.$set(patient, 'healthCondition', {})
      }

      if (!Object.prototype.hasOwnProperty.call(patient.healthCondition, 'patientState')) {
        this.$set(patient.healthCondition, 'patientState', null)
      }
    },
    resetPeselValidation() {
      this.$nextTick(() => {
        const provider = this.$refs.peselValidationProvider
        if (provider && typeof provider.reset === 'function') {
          provider.reset()
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.order-patient-form__validation-message {
  margin-top: 0.35rem;
  margin-bottom: 0.5rem;
}

.order-patient-form__checkbox {
  margin-top: 0;
}
</style>
